import {
  ApiResponse,
  UserListResponse,
  UserDetailResponse,
  MessageResponse,
  PayCenterCardPropertiesResponse,
  PayCenterCardSatisfiedResponse,
  UpdateUserDetailsRequest,
  UpdateUserDetailsResponse,
  BankAccount,
  UserImportRequest,
  UserImportResponse,
  CryptoWallet,
  TradevestOnboardingLog,
  TradevestRetryResponse,
  TradevestResetResponse,
  TradevestSetStatusResponse,
  TradevestUserPortfolioSummary,
  TradevestBucket,
  DevicePresenceResponse,
  DevicePingResponse,
  StaffTradevestTaxResponse,
  StaffCancelFreistellungsauftragResponse,
  StaffCreateFreistellungsauftragResponse,
  StaffUpdateFreistellungsauftragResponse,
  CorporateActionListResponse,
  StaffFreistellungsauftragRequest,
  StaffUpdateUserEmailRequest,
  StaffUpdateUserEmailResponse,
  TradevestSupportGuidanceResponse,
} from '@mygate/common';
import { api } from './api';

class UsersService {
  /**
   * Fetch all users (with high page size for rich client)
   */
  async fetchAllUsers(): Promise<ApiResponse<UserListResponse>> {
    // Fetch all users at once for client-side operations
    return api.get('/api/staff/users?pageSize=10000&page=1');
  }

  /**
   * Fetch users with specific parameters
   */
  async fetchUsers(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
    plan?: string;
    paycenterIdFilter?: 'has' | 'none';
    hasConstraints?: boolean;
    kycStatus?: string;
    tradevestBucket?: TradevestBucket;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<UserListResponse>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return api.get(`/api/staff/users${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get detailed user information
   */
  async getUserDetails(userId: string): Promise<ApiResponse<UserDetailResponse>> {
    return api.get(`/api/staff/users/${userId}`);
  }

  /**
   * Suspend a user account
   */
  async suspendUser(userId: string, reason: string): Promise<ApiResponse<MessageResponse>> {
    return api.put(`/api/staff/users/${userId}/suspend`, { reason });
  }

  /**
   * Activate a suspended user account
   */
  async activateUser(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.put(`/api/staff/users/${userId}/activate`);
  }

  /**
   * Set user status to pending verification
   */
  async setPendingVerification(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.put(`/api/staff/users/${userId}/set-pending-verification`);
  }

  /**
   * Reset user's PIN
   */
  async resetUserPin(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.post(`/api/staff/users/${userId}/reset-pin`);
  }

  /**
   * Detach KYC from user
   */
  async detachKyc(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.post(`/api/staff/users/${userId}/detach-kyc`);
  }

  /**
   * Delete user account
   */
  async deleteUser(userId: string, reason: string, permanentDelete = false): Promise<ApiResponse<MessageResponse>> {
    return api.request(`/api/staff/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, permanentDelete }),
    });
  }

  /**
   * Get user activity logs
   */
  async getUserActivity(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.get(`/api/staff/users/${userId}/activity`);
  }

  /**
   * Trigger crypto provisioning for user
   */
  async provisionCrypto(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.post(`/api/staff/users/${userId}/provision-crypto`);
  }

  /**
   * Trigger PayCenter registration for user
   */
  async registerPayCenter(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.post(`/api/staff/users/${userId}/register-paycenter`);
  }

  /**
   * Get user's card property validation data from PayCenter
   */
  async getCardPropertyValidation(userId: string): Promise<ApiResponse<PayCenterCardPropertiesResponse>> {
    return api.get(`/api/staff/users/${userId}/cards/property-validation`);
  }

  /**
   * Get user's plastic card satisfied status from PayCenter
   */
  async getPlasticCardSatisfied(userId: string): Promise<ApiResponse<PayCenterCardSatisfiedResponse>> {
    return api.get(`/api/staff/users/${userId}/cards/plastic-satisfied`);
  }

  /**
   * Update user details (firstName, lastName, title)
   */
  async updateUserDetails(
    userId: string,
    data: UpdateUserDetailsRequest,
  ): Promise<ApiResponse<UpdateUserDetailsResponse>> {
    return api.request(`/api/staff/users/${userId}/details`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Correct the email address on file (Admin + Support).
   *
   * Separate from updateUserDetails: the app refuses to change a confirmed address, so this
   * is the only route for such a request, and it carries a mandatory reason for the audit log.
   * @backend user-management.controller.ts → updateUserEmail
   */
  async updateUserEmail(
    userId: string,
    data: StaffUpdateUserEmailRequest,
  ): Promise<ApiResponse<StaffUpdateUserEmailResponse>> {
    return api.request(`/api/staff/users/${userId}/email`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get user's bank accounts (including balance)
   */
  async getUserAccounts(userId: string): Promise<ApiResponse<BankAccount[]>> {
    return api.get(`/api/staff/users/${userId}/accounts`);
  }

  /**
   * Sync PayCenter card properties for all users
   * Admin only
   */
  async syncCardProperties(params?: {
    forceRefresh?: boolean;
    batchSize?: number;
    delayMs?: number;
  }): Promise<ApiResponse<{ message: string }>> {
    return api.post('/api/staff/users/sync-card-properties', params || {});
  }

  /**
   * Deactivate PayCenter account for user
   * Admin only
   */
  async deactivatePayCenterAccount(userId: string): Promise<ApiResponse<MessageResponse>> {
    return api.post(`/api/staff/users/${userId}/deactivate-paycenter`);
  }

  /**
   * Import users from CSV data
   * Admin only
   */
  async importUsers(request: UserImportRequest): Promise<ApiResponse<UserImportResponse>> {
    return api.post('/api/staff/users/import', request);
  }

  /**
   * Get user's crypto wallet balances
   */
  async getUserWallets(userId: string): Promise<ApiResponse<CryptoWallet[]>> {
    return api.get(`/api/staff/users/${userId}/wallets`);
  }

  /**
   * Get user's Tradevest onboarding log
   * Returns the full log document with all events for the user's Tradevest onboarding process
   */
  async getTradevestOnboardingLog(userId: string): Promise<ApiResponse<TradevestOnboardingLog | null>> {
    return api.get(`/api/staff/users/${userId}/tradevest-log`);
  }

  /**
   * What staff should do next about the user's unfinished Tradevest onboarding.
   * @backend tradevest-onboarding-oversight.controller.ts → getUserGuidance
   * @ui UserDetailModal.tsx → TradevestGuidanceBlock
   */
  async getTradevestGuidance(userId: string): Promise<ApiResponse<TradevestSupportGuidanceResponse>> {
    return api.get(`/api/staff/users/${userId}/tradevest-guidance`);
  }

  /**
   * Retry Tradevest onboarding from current stuck state.
   * @backend tradevest-onboarding.service.ts → retryOnboarding() produces TradevestRetryStep[]
   * @ui UserDetailModal.tsx → TradevestRetryModal renders the steps array as a Timeline
   */
  async retryTradevestOnboarding(userId: string): Promise<ApiResponse<TradevestRetryResponse>> {
    return api.post(`/api/staff/users/${userId}/tradevest-retry`);
  }

  /**
   * Reset all Tradevest onboarding data so the user can restart from mobile.
   * @backend tradevest-onboarding.service.ts → resetOnboarding() clears all Tradevest fields
   * @ui UserDetailModal.tsx → Reset button handler uses this
   */
  async resetTradevestOnboarding(userId: string): Promise<ApiResponse<TradevestResetResponse>> {
    return api.post(`/api/staff/users/${userId}/tradevest-reset`);
  }

  /**
   * Set Tradevest provisioning status — manual override for stepping back users.
   * @backend user-management.controller.ts → setTradevestProvisioningStatus
   * @ui UserDetailModal.tsx → Set Status modal
   */
  async setTradevestProvisioningStatus(
    userId: string,
    status: string,
    reason: string,
    clearError: boolean,
  ): Promise<ApiResponse<TradevestSetStatusResponse>> {
    return api.post(`/api/staff/users/${userId}/tradevest-set-status`, { status, reason, clearError });
  }

  /**
   * Get user's live Tradevest portfolio (current EUR value + 24h change vs latest snapshot).
   * @backend user-management.controller.ts → getTradevestPortfolio
   * @ui UserDetailModal.tsx → Tradevest section value + change %
   */
  async getTradevestPortfolio(userId: string): Promise<ApiResponse<TradevestUserPortfolioSummary>> {
    return api.get(`/api/staff/users/${userId}/tradevest-portfolio`);
  }

  /**
   * German tax overview for a Tradevest customer: withheld tax, Verlusttöpfe and every
   * Freistellungsauftrag on file, for the given tax year.
   * @backend tradevest-tax.controller.ts → getTaxOverview
   */
  async getTradevestTaxes(userId: string, year?: number): Promise<ApiResponse<StaffTradevestTaxResponse>> {
    const query = year ? `?year=${year}` : '';
    return api.get(`/api/staff/users/${userId}/tradevest-taxes${query}`);
  }

  /**
   * The user's full corporate action history, including the elective events the app hides.
   * @backend tradevest-tax.controller.ts → getCorporateActions
   */
  async getTradevestCorporateActions(userId: string): Promise<ApiResponse<CorporateActionListResponse>> {
    return api.get(`/api/staff/users/${userId}/tradevest-corporate-actions`);
  }

  /**
   * Create a Freistellungsauftrag for the user (Admin only). The app is read-only.
   * @backend tradevest-tax.controller.ts → createFreistellungsauftrag
   */
  async createFreistellungsauftrag(
    userId: string,
    body: StaffFreistellungsauftragRequest,
  ): Promise<ApiResponse<StaffCreateFreistellungsauftragResponse>> {
    return api.post(`/api/staff/users/${userId}/tradevest-fsa`, body);
  }

  /**
   * Update an existing Freistellungsauftrag's amount or validity (Admin only).
   * @backend tradevest-tax.controller.ts → updateFreistellungsauftrag
   */
  async updateFreistellungsauftrag(
    userId: string,
    taxExemptionOrderId: string,
    body: { fsaAmount?: number; fsaValidUntil?: string; reason?: string },
  ): Promise<ApiResponse<StaffUpdateFreistellungsauftragResponse>> {
    return api.patch(`/api/staff/users/${userId}/tradevest-fsa/${taxExemptionOrderId}`, body);
  }

  /**
   * Cancel (kündigen) a Freistellungsauftrag (Admin only). Upstream this is a PATCH of
   * `fsaValidUntil`; how far back it reaches depends on whether the allowance was ever used.
   * @backend tradevest-tax.controller.ts → cancelFreistellungsauftrag
   */
  async cancelFreistellungsauftrag(
    userId: string,
    taxExemptionOrderId: string,
    reason?: string,
  ): Promise<ApiResponse<StaffCancelFreistellungsauftragResponse>> {
    return api.request(`/api/staff/users/${userId}/tradevest-fsa/${taxExemptionOrderId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  async getUserPresence(userId: string): Promise<ApiResponse<DevicePresenceResponse>> {
    return api.get(`/api/staff/users/${userId}/presence`);
  }

  async pingUserDevice(userId: string): Promise<ApiResponse<DevicePingResponse>> {
    return api.post(`/api/staff/users/${userId}/ping`);
  }
}

export const usersService = new UsersService();
